//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { DATE_FORMAT_MM_DD_YYYY } from '@campaigns/constants/DateConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import DatePicker from '@components/DatePicker';
import { BodyText } from '@components/atoms/text/Text';
import TextInput from '@components/atoms/text-input/TextInput';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import TimeZoneId from '@models/TimeZoneId';
import { isDefined, isUndefined } from '@utils/filterPredicates';

const getHelpText = (
  isDeals: boolean,
  isClientProvided: boolean,
  isBVShipping: boolean,
) => {
  if (isDeals) {
    return isClientProvided
      ? "Date when participants can use product's code on your website"
      : 'Date when product will be available for purchase';
  }

  return isBVShipping
    ? 'Date when you can ship products to our warehouse'
    : 'Date when you can ship products to community members';
};

interface ShippingAvailabilityDateFieldProps {
  isViewOnly?: boolean;
}

export const ShippingAvailabilityDateField = ({
  isViewOnly = false,
}: ShippingAvailabilityDateFieldProps) => {
  const { samplingCampaignRequest } = useSamplingCampaign();
  const { control, watch } = useFormContext<EditableBVSamplingProduct>();

  const whoShipsProduct =
    samplingCampaignRequest?.fulfillmentOptions?.whoShipsProduct;
  const isBVShipping = whoShipsProduct === 'bazaarvoice';
  const isDeals =
    samplingCampaignRequest?.fulfillmentOptions?.fulfillmentType === 'deals';
  const isClientProvided =
    samplingCampaignRequest?.fulfillmentOptions?.codeSupplier === 'client';

  const fieldLabels = {
    label: isBVShipping
      ? 'Shipping Availability Date (optional)'
      : 'Product Availability Date (optional)',
    helpText: getHelpText(isDeals, isClientProvided, isBVShipping),
  };

  const rawDate = watch('shippingAvailabilityDate');
  const formattedDate = moment(rawDate).utc().format(DATE_FORMAT_MM_DD_YYYY);

  if (isViewOnly) {
    return (
      <ViewOnlyContainer>
        <BodyText>{fieldLabels.label}</BodyText>
        <BodyText>{rawDate ? formattedDate : DEFAULT_EMPTY_VALUE}</BodyText>
      </ViewOnlyContainer>
    );
  }

  return (
    <FormFieldController
      control={control}
      name='shippingAvailabilityDate'
      rules={{
        validate: (value) => {
          if (isUndefined(value)) {
            return undefined;
          }

          // Validate the date format and ensure it's a valid date
          const selectedDate = moment.utc(value);
          if (!selectedDate.isValid()) {
            return 'Please enter a valid date';
          }

          return true;
        },
      }}
    >
      {({ onChange, value, ...props }) => (
        <StyledEndDateInputWrapper>
          <DatePicker
            {...props}
            // Use UTC timezone to ensure consistent date handling across all timezones
            // This aligns with BV's backend approach where dates are stored in UTC
            customTarget={
              <TextInput
                {...props}
                isDisabled={isViewOnly}
                label={fieldLabels.label}
                labelHelpText={fieldLabels.helpText}
                placeholder={DATE_FORMAT_MM_DD_YYYY}
                trackingId={`${SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD}-shipping-availability-date`}
                value={
                  isDefined(value)
                    ? moment(value)
                        // Interpret the date in UTC to prevent timezone shifts
                        // and maintains consistency with BV's backend processing
                        .utc()
                        .format(DATE_FORMAT_MM_DD_YYYY)
                    : ''
                }
              />
            }
            handleDateChanged={onChange}
            timeZone={TimeZoneId.Utc}
            value={value}
          />
        </StyledEndDateInputWrapper>
      )}
    </FormFieldController>
  );
};

const ViewOnlyContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[1]};
  `}
`;

const StyledEndDateInputWrapper = styled.div`
  flex-direction: row;
  display: flex;
  gap: ${({ theme }) => theme.spacings[1]};
`;
